import type { AppConfig } from '@ms-cloudpack/common-types';
import { expandModulePaths } from './expandModulePaths.js';
import { describe, expect, it } from '@jest/globals';

describe('expandModulePaths', () => {
  const mockConfigPath = '/path/to/project/cloudpack.config.json';
  const configDir = '/path/to/project';

  it('expands {configDir} template variable in module paths', () => {
    const appConfig: AppConfig = {
      resolve: {
        modules: ['{configDir}/store', '{configDir}/components', 'node_modules'],
      },
    };

    expandModulePaths({ appConfig, configPath: mockConfigPath });

    expect(appConfig.resolve?.modules).toEqual([`${configDir}/store`, `${configDir}/components`, 'node_modules']);
  });

  it('handles mixed paths with and without {configDir} template', () => {
    const appConfig: AppConfig = {
      resolve: {
        modules: ['{configDir}/src', 'node_modules', '{configDir}/lib'],
      },
    };

    expandModulePaths({ appConfig, configPath: mockConfigPath });

    expect(appConfig.resolve?.modules).toEqual([`${configDir}/src`, 'node_modules', `${configDir}/lib`]);
  });

  it('throws error when {configDir} is not at the beginning', () => {
    const appConfig: AppConfig = {
      resolve: {
        modules: ['some/path/{configDir}/nested'],
      },
    };

    expect(() => {
      expandModulePaths({ appConfig, configPath: mockConfigPath });
    }).toThrow(
      '{configDir} template variable is only allowed at the beginning of a `resolve.modules` path. Received: "some/path/{configDir}/nested"',
    );
  });

  it('throws error when {configDir} is at the end', () => {
    const appConfig: AppConfig = {
      resolve: {
        modules: ['another/{configDir}'],
      },
    };

    expect(() => {
      expandModulePaths({ appConfig, configPath: mockConfigPath });
    }).toThrow(
      '{configDir} template variable is only allowed at the beginning of a `resolve.modules` path. Received: "another/{configDir}"',
    );
  });

  it('does not modify paths without {configDir} template', () => {
    const appConfig: AppConfig = {
      resolve: {
        modules: ['node_modules', '../shared', '/absolute/path'],
      },
    };

    expandModulePaths({ appConfig, configPath: mockConfigPath });

    expect(appConfig.resolve?.modules).toEqual(['node_modules', '../shared', '/absolute/path']);
  });

  it('handles empty modules array', () => {
    const appConfig: AppConfig = {
      resolve: {
        modules: [],
      },
    };

    expandModulePaths({ appConfig, configPath: mockConfigPath });

    expect(appConfig.resolve?.modules).toEqual([]);
  });

  it('handles missing resolve config', () => {
    const appConfig: AppConfig = {};

    expect(() => {
      expandModulePaths({ appConfig, configPath: mockConfigPath });
    }).not.toThrow();
  });

  it('handles missing modules in resolve config', () => {
    const appConfig: AppConfig = {
      resolve: {},
    };

    expect(() => {
      expandModulePaths({ appConfig, configPath: mockConfigPath });
    }).not.toThrow();
  });

  it('works with nested config directory paths', () => {
    const nestedConfigPath = '/deeply/nested/path/to/config/cloudpack.config.json';
    const nestedConfigDir = '/deeply/nested/path/to/config';

    const appConfig: AppConfig = {
      resolve: {
        modules: ['{configDir}/lib', '{configDir}/utils'],
      },
    };

    expandModulePaths({ appConfig, configPath: nestedConfigPath });

    expect(appConfig.resolve?.modules).toEqual([`${nestedConfigDir}/lib`, `${nestedConfigDir}/utils`]);
  });

  it('throws error when configPath is not absolute', () => {
    const appConfig: AppConfig = {
      resolve: {
        modules: ['{configDir}/lib'],
      },
    };

    expect(() => {
      expandModulePaths({ appConfig, configPath: 'relative/path/cloudpack.config.json' });
    }).toThrow('configPath must be an absolute path, got: "relative/path/cloudpack.config.json"');
  });

  it('throws error when multiple {configDir} templates exist in the same path', () => {
    const appConfig: AppConfig = {
      resolve: {
        modules: ['{configDir}/{configDir}/something'],
      },
    };

    expect(() => {
      expandModulePaths({ appConfig, configPath: mockConfigPath });
    }).toThrow(
      '{configDir} template variable is only allowed at the beginning of a `resolve.modules` path. Received: "/path/to/project/{configDir}/something"',
    );
  });
});
